import React from 'react';
import { Tooltip } from 'antd';
import { useAccessControl } from '../../hooks/usePermission';
import { PermissionCode } from '../../constants/permissionCodes';

const DISABLEABLE_TAGS = ['button', 'input', 'select', 'textarea', 'option'];
const ACTIVATION_KEYS = ['Enter', ' ', 'Spacebar'];

export interface ICanProps {
  permission?: PermissionCode;
  anyOf?: PermissionCode[];
  mode?: 'disable' | 'hide';
  tooltip?: string;
  fallback?: React.ReactNode;
  children: React.ReactElement<any>;
}

export const Can: React.FC<ICanProps> = ({
  permission,
  anyOf,
  mode = 'disable',
  tooltip = 'You do not have permission for this action',
  fallback = null,
  children,
}) => {
  const { can, canAny } = useAccessControl();

  const allowed =
    (permission ? can(permission) : false) ||
    (anyOf?.length ? canAny(anyOf) : false);

  if (allowed) return children;

  if (mode === 'hide') {
    return fallback as React.ReactElement | null;
  }

  const isElement = React.isValidElement<{ style?: React.CSSProperties; disabled?: boolean }>(children);
  const childType = isElement ? children.type : null;
  const supportsDisabled =
    childType !== null && (typeof childType !== 'string' || DISABLEABLE_TAGS.includes(childType));

  const inertChild =
    isElement && supportsDisabled
      ? React.cloneElement(children, {
          disabled: true,
          style: { ...children.props.style, pointerEvents: 'none' },
        })
      : children;

  const swallowActivation = (event: React.KeyboardEvent) => {
    if (ACTIVATION_KEYS.includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
  };

  return (
    <Tooltip title={tooltip}>
      <span style={{ display: 'inline-flex', cursor: 'not-allowed' }} onKeyDownCapture={swallowActivation}>
        <span style={{ pointerEvents: 'none' }}>{inertChild}</span>
      </span>
    </Tooltip>
  );
};

export default Can;
